import { Stack } from "@mui/material";
import type { GridColumnVisibilityModel } from "@mui/x-data-grid-premium";
import { useEffect, useMemo, useRef, useState } from "react";
import type { WorkflowStatus } from "@entrilia/shared/api/workflowTypes";
import { useGridDataSourceWithRange } from "@entrilia/shared/components/grid/gridHooks";
import adminApi from "../../../../api/adminApi";
import type { WorkflowFieldSchemaDefinition } from "../../../../api/types/workflowTypes";
import useClientContext from "../../../../context/useClientContext";
import { FilterContextProvider } from "../../../common/filters/FilterContextProvider";
import FiltersPanel from "../../../common/filters/FiltersPanel";
import { getInitialFilterState } from "../../../common/filters/filterState";
import WorkflowRunsGrid from "./WorkflowRunsGrid";
import { getWorkflowRunsFilterDefinitions, getWorkflowsFilterPayload } from "./workflowRunsListFilters";

interface Props {
  target: "active" | "completed" | "failed" | "need_action";
  templateId: string;
  workflowFieldsSchema: WorkflowFieldSchemaDefinition[];
  refreshTrigger?: number;
}

const WorkflowRunsList = ({ target, templateId, workflowFieldsSchema, refreshTrigger }: Props) => {
  const { clientCode } = useClientContext();

  const filterId = useMemo(() => `${clientCode}_workflow_runs_${target}_filter_v2`, [clientCode, target]);

  const statuses = useMemo<WorkflowStatus[]>(() => {
    switch (target) {
      case "active":
      case "need_action":
        return ["InProgress"];
      case "completed":
        return ["Completed", "Rejected", "Cancelled"];
      case "failed":
        return ["Failed"];
    }
  }, [target]);

  const gridColumnVisibilityModel = useMemo<GridColumnVisibilityModel>(
    () => ({
      status: target === "active" || target === "completed",
      currentActivityName: target === "active" || target === "need_action",
      completedAt: target === "completed" || target === "failed",
    }),
    [target]
  );

  const [filterState, setFilterState] = useState(() =>
    getInitialFilterState(filterId, getWorkflowRunsFilterDefinitions(workflowFieldsSchema), [])
  );

  const filterPayload = useMemo(
    () =>
      getWorkflowsFilterPayload(
        filterState,
        statuses,
        templateId,
        target === "need_action" ? "WaitingForUserSignal" : undefined
      ),
    [filterState, statuses, templateId, target]
  );

  const { gridApiRef, dataSource, totalRows, isLoading, isRefreshing, refreshDataSource } = useGridDataSourceWithRange(
    adminApi.searchWorkflows,
    filterPayload,
    25
  );

  const prevTriggerRef = useRef(refreshTrigger);

  useEffect(() => {
    if (refreshTrigger !== prevTriggerRef.current) {
      prevTriggerRef.current = refreshTrigger;
      refreshDataSource();
    }
  }, [refreshTrigger, refreshDataSource]);

  return (
    <FilterContextProvider filterState={filterState} setFilterState={setFilterState}>
      <Stack spacing={2} sx={{ height: "100%" }}>
        <FiltersPanel
          recordCount={totalRows ?? 0}
          isLoading={isLoading}
          isRefreshing={isRefreshing}
          onRefresh={refreshDataSource}
          withoutSearch
        />

        <WorkflowRunsGrid
          apiRef={gridApiRef}
          dataSource={dataSource}
          filterId={filterId}
          workflowFieldsSchema={workflowFieldsSchema}
          columnVisibilityModel={gridColumnVisibilityModel}
        />
      </Stack>
    </FilterContextProvider>
  );
};

export default WorkflowRunsList;
